import React, { useState, useEffect } from "react";
import jsPDF from "jspdf";
import "jspdf-autotable";
import "./ServiceDeliveryReport.css"; // Import the CSS for styling
import { DownloadOutlined } from "@ant-design/icons";

const ServiceDeliveryReport = () => {
  // Static data
  const defaultConsignmentDetails = {
    consignmentNumber: "12345",
    status: "Delivered",
    date: "01/09/2024",
    driver: "John Doe",
    address:
      "123 Main St, Springfield, 12345,123 Main St, Springfield, 12345, IL123 Main St, Springfield, 12345, IL 123 Main St, Springfield, 12345, IL",
    zone: "North Zone",
    suburb: "Springfield",
    postcode: "12345",
    state: "IL",
  };

  const defaultKeyAchievements = [
    "Customer appreciated timely delivery.",
    "Major improvement in delivery time.",
  ];

  const defaultActionsPlanned = [
    "Implement new tracking system.",
    "Review and optimize delivery routes.",
    "Prepare for upcoming audit.",
  ];

  // State variables to store API data
  const [consignmentDetails, setConsignmentDetails] = useState(
    defaultConsignmentDetails
  );
  const [keyAchievements, setKeyAchievements] = useState(
    defaultKeyAchievements
  );
  const [actionsPlanned, setActionsPlanned] = useState(defaultActionsPlanned);

  // Function to fetch data from API
  const fetchData = async () => {
    try {
      // Replace with your actual API endpoint
      const response = await fetch("https://api.example.com/report-data");
      const data = await response.json();

      // Update state with the fetched data
      setConsignmentDetails(
        data.consignmentDetails || defaultConsignmentDetails
      );
      setKeyAchievements(data.keyAchievements || defaultKeyAchievements);
      setActionsPlanned(data.actionsPlanned || defaultActionsPlanned);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  // Fetch data when component mounts
  useEffect(() => {
    fetchData();
  });

  // const downloadCSV = async () => {
  //   try {
  //     const response = await fetch("https://api.example.com/report-csv", {
  //       method: "GET", // Adjust based on your API method (GET, POST, etc.)
  //     });

  //     // If response is OK, proceed
  //     if (response.ok) {
  //       const blob = await response.blob();
  //       const url = window.URL.createObjectURL(new Blob([blob]));

  //       // Create a link element and simulate a click to download the file
  //       const link = document.createElement("a");
  //       link.href = url;
  //       link.setAttribute("download", "LateReturnReport.csv"); // Set filename
  //       document.body.appendChild(link);
  //       link.click();
  //       link.parentNode.removeChild(link); // Remove the link element
  //     } else {
  //       console.error("Failed to download CSV file.");
  //     }
  //   } catch (error) {
  //     console.error("Error fetching CSV file:", error);
  //   }
  // };

  const downloadPDF = () => {
    const doc = new jsPDF();

    // Get page dimensions
    const pageWidth = doc.internal.pageSize.getWidth(); // Page width in mm
    const pageHeight = doc.internal.pageSize.getHeight(); // Page height in mm

    // Define margins as percentages of page dimensions
    const marginX = pageWidth * 0.05; // 5% of page width
    const marginY = pageHeight * 0.05; // 5% of page height
    const contentWidth = pageWidth - 2 * marginX; // Width of content area

    let currentY = marginY; // Start Y position from top margin

    // Function to draw title with fixed background color and font size
    const drawTitle = (text, yPosition) => {
      const titleHeight = 4; // Title height in mm
      const bgColor = [221, 221, 221]; // Background color #ddd in RGB

      // Draw background rectangle with fixed dimensions
      doc.setFillColor(...bgColor);
      doc.rect(
        marginX - 6,
        yPosition - 6,
        contentWidth + 8,
        titleHeight + 8,
        "F"
      );

      // Draw title text with fixed font size
      doc.setFont("Helvetica Light", "bold");
      doc.setFontSize(12); // Fixed font size in pt
      doc.setTextColor(0, 0, 0); // Text color black
      doc.text(text, pageWidth / 2, yPosition + titleHeight / 2, {
        align: "center",
      });

      // Update currentY position for the next section
      return yPosition + titleHeight + 10;
    };

    // Title Section
    currentY = drawTitle("Late Return Report", currentY);

    // Consignment Details Table Data
    const tableColumns = [
      { header: "Consignment Number", dataKey: "consignmentNumber" },
      { header: "Status", dataKey: "status" },
      { header: "Date", dataKey: "date" },
      { header: "Driver", dataKey: "driver" },
      { header: "Address", dataKey: "address" },
      { header: "Zone", dataKey: "zone" },
      { header: "Suburb", dataKey: "suburb" },
      { header: "Postcode", dataKey: "postcode" },
      { header: "State", dataKey: "state" },
    ];

    const tableData = [
      {
        consignmentNumber: consignmentDetails.consignmentNumber,
        status: consignmentDetails.status,
        date: consignmentDetails.date,
        driver: consignmentDetails.driver,
        address: consignmentDetails.address,
        zone: consignmentDetails.zone,
        suburb: consignmentDetails.suburb,
        postcode: consignmentDetails.postcode,
        state: consignmentDetails.state,
      },
    ];

    // Generate table using jspdf-autotable
    doc.autoTable({
      columns: tableColumns,
      body: tableData,
      startY: currentY,
      margin: { left: marginX, right: marginX },
      styles: {
        fontSize: 12, // Font size in pt
        cellPadding: 1, // Padding within cells
        lineWidth: 0.1, // Line width for cell borders
        lineColor: [0, 0, 0], // Border color (black)
      },
      headStyles: {
        fillColor: [200, 200, 200], // Header background color
        textColor: [0, 0, 0], // Header text color
        lineWidth: 0.1, // Line width for header borders
        lineColor: [0, 0, 0], // Border color (black)
      },
      theme: "grid", // Use grid theme for table lines
    });

    // Update currentY after table
    currentY = doc.autoTable.previous.finalY + 10;

    // Key Achievements Section
    currentY = drawTitle("1.1 Key Achievements in Last Week", currentY);
    doc.setFontSize(12); // Fixed font size in pt
    doc.setFont("Helvetica Light", "normal");
    keyAchievements.forEach((achievement, index) => {
      doc.text(`${index + 1}. ${achievement}`, marginX + 5, currentY);
      currentY += 7; // Increment Y position for next line
    });

    currentY += 10;

    // Actions Planned Section
    currentY = drawTitle("1.2 Actions Planned for Next Week", currentY);
    doc.setFontSize(12); // Set font size for normal text
    doc.setFont("Helvetica Light", "normal");
    actionsPlanned.forEach((action, index) => {
      doc.text(`${index + 1}. ${action}`, marginX + 5, currentY);
      currentY += 7;
    });

    currentY += 20;

    // Footer Section
    doc.setLineWidth(0.5);
    doc.line(marginX, currentY, pageWidth - marginX, currentY); // Line above footer

    currentY += 10;
    doc.setFontSize(10); // Fixed font size in pt
    doc.setFont("Helvetica Light", "normal");
    doc.text("Document No: 001", marginX, currentY);
    doc.text("Revision No: 1", marginX + 60, currentY);
    doc.text("Sheet: 2 of 6", pageWidth - 60, currentY);
    doc.text("Issue Date: 01-09-2024", pageWidth - 60, currentY + 10);

    // Save the PDF
    doc.save("LateReturnReport.pdf");
  };

  return (
    <>
      <div className="container mt-5">
        <div className="title">Late Return Report</div>

        <table className="table ">
          <tbody>
            {/* Row 1: Consignment Number and Status */}
            <tr>
              <th>Consignment Number:</th>
              <td>{consignmentDetails.consignmentNumber}</td>
              <th>Status:</th>
              <td>{consignmentDetails.status}</td>
            </tr>

            {/* Row 2: Date and Driver */}
            <tr>
              <th>Date:</th>
              <td>{consignmentDetails.date}</td>
              <th>Driver:</th>
              <td>{consignmentDetails.driver}</td>
            </tr>

            {/* Row 3: Address */}
            <tr>
              <th>Address:</th>
              <td colSpan="3">{consignmentDetails.address}</td>
            </tr>

            {/* Row 4: Zone and Suburb */}
            <tr>
              <th>Zone:</th>
              <td>{consignmentDetails.zone}</td>
              <th>Suburb:</th>
              <td>{consignmentDetails.suburb}</td>
            </tr>

            {/* Row 5: Postcode and State */}
            <tr>
              <th>Postcode:</th>
              <td>{consignmentDetails.postcode}</td>
              <th>State:</th>
              <td>{consignmentDetails.state}</td>
            </tr>
          </tbody>
        </table>

        <div className="Key">
          <section className="achievements-box">
            <h2>1.1 Key Achievements in Last Week</h2>
            <ul>
              {keyAchievements.map((achievement, index) => (
                <li key={index}>{achievement}</li>
              ))}
            </ul>
          </section>
        </div>

        <div className="kye">
          <section className="achievements-box">
            <h2>1.2 Actions Planned for Next Week</h2>
            <ul>
              {actionsPlanned.map((action, index) => (
                <li key={index}>{action}</li>
              ))}
            </ul>
          </section>
        </div>

        <footer>
          <div>Document No: 001</div>
          <div>Revision No: 1</div>
          <div>Sheet: 2 of 6</div>
          <div>Issue Date: 01-09-2024</div>
        </footer>

        {/* Download PDF Button */}
        <button className="downloadbtn" onClick={downloadPDF}>
          <span style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <DownloadOutlined style={{ fontSize: "16px" }} /> Download PDF
          </span>
        </button>
      </div>
    </>
  );
};

export default ServiceDeliveryReport;
